import api from './api';
import type {
  AnnotationTask,
  TaskListItem,
  Utterance,
  Entity,
  Photo,
  PhotoLabels,
  PhotoType,
  TaskReview,
  SupervisorAssignment,
  TaskStatus,
  TranscriptionJob,
  AutoUtterance,
} from '../types/annotation';

// ─── Tasks ─────────────────────────────────────────────────────────────────────

export interface ListTasksParams {
  status?: TaskStatus;
  projectId?: string;
  enumeratorId?: string;
  // Substring filters resolved server-side (span all pages).
  enumerator?: string;
  language?: string;
  transcribedBy?: string;
  sessionId?: string;
  sortBy?: 'priority' | 'createdAt' | 'updatedAt' | 'transcribedAt' | 'duration' | 'language' | 'project' | 'transcribedBy';
  sortDir?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

export interface ListTasksResponse {
  tasks: TaskListItem[];
  total: number;
  limit: number;
  offset: number;
}

export const listTasks = (params?: ListTasksParams): Promise<ListTasksResponse> =>
  api.get('/annotation/tasks', { params }).then((r) => {
    const d = r.data.data ?? {};
    return {
      tasks: d.tasks ?? [],
      total: typeof d.total === 'number' ? d.total : (d.tasks?.length ?? 0),
      limit: typeof d.limit === 'number' ? d.limit : (params?.limit ?? 50),
      offset: typeof d.offset === 'number' ? d.offset : (params?.offset ?? 0),
    };
  });

// ─── Analytics: Leaderboards ───────────────────────────────────────────────────

export interface LeaderboardRowUser {
  userId: string;
  name?: string;
  email?: string;
  role?: string;
  phone?: string;
}

export interface UploadLeaderboardRow extends LeaderboardRowUser {
  totalSessions: number;
  recordingSeconds: number;
}

export interface TranscribeLeaderboardRow extends LeaderboardRowUser {
  submittedSessions: number;
  transcribedSeconds: number;
  approvedSessions: number;
  approvedSeconds: number;
}

export interface LeaderboardsResponse {
  uploads: {
    summary: { totalSessions: number; recordingSeconds: number };
    rows: UploadLeaderboardRow[];
  };
  transcribes: {
    summary: {
      submittedSessions: number;
      transcribedSeconds: number;
      approvedSessions: number;
      approvedSeconds: number;
    };
    rows: TranscribeLeaderboardRow[];
  };
}

export const getLeaderboards = (params?: {
  from?: string;
  to?: string;
}): Promise<LeaderboardsResponse> =>
  api.get('/annotation/analytics/leaderboards', { params }).then((r) => r.data.data);

export const createTask = (data: {
  sessionId: string;
  enumeratorId?: string;
  supervisorId?: string;
}): Promise<AnnotationTask> =>
  api.post('/annotation/tasks', data).then((r) => r.data.data);

export const getTask = (taskId: string): Promise<AnnotationTask> =>
  api.get(`/annotation/tasks/${taskId}`).then((r) => {
    const d = r.data.data;
    return {
      ...d.task,
      utterances: d.utterances ?? [],
      photos: d.photos ?? [],
      transcripts: d.transcripts ?? [],
      latestReview: d.latestReview ?? undefined,
      videoUrl: d.videoUrl ?? undefined,
      audioUrl: d.audioUrl ?? undefined,
      peaksUrl: d.peaksUrl ?? undefined,
    } as AnnotationTask;
  });

export const updateTaskStatus = (taskId: string, status: TaskStatus): Promise<AnnotationTask> =>
  api.put(`/annotation/tasks/${taskId}/status`, { status }).then((r) => r.data.data);

export const submitTask = (taskId: string): Promise<AnnotationTask> =>
  api.post(`/annotation/tasks/${taskId}/submit`).then((r) => r.data.data);

export const reviewTask = (
  taskId: string,
  data: { status: 'approved' | 'rejected' | 'revision_needed'; comments?: string },
): Promise<TaskReview> =>
  api.post(`/annotation/tasks/${taskId}/review`, data).then((r) => r.data.data);

// Sub-admin (or admin) final-tier review. 'revision_needed' here bounces back
// to the supervisor; 'final_accepted' / 'final_rejected' are terminal.
export const finalReviewTask = (
  taskId: string,
  data: { status: 'final_accepted' | 'final_rejected' | 'revision_needed'; comments?: string },
): Promise<TaskReview> =>
  api.put(`/annotation/tasks/${taskId}/final-review`, data).then((r) => r.data.data);

// Admin-only: rescue a task from a terminal rejected state.
// target = where the task lands after reopening (validated server-side based
// on the from-status: from 'rejected' allowed → submitted / revision_needed / approved;
// from 'final_rejected' allowed → submitted / approved). Comments are required.
export const reopenTask = (
  taskId: string,
  data: { target: 'submitted' | 'revision_needed' | 'approved'; comments: string },
): Promise<TaskReview> =>
  api.put(`/annotation/tasks/${taskId}/reopen`, data).then((r) => r.data.data);

// Claim/unclaim — set or clear annotation_tasks.claimed_by. Once claimed,
// only the claimer (plus supervisor + admin) can edit. Unclaim is supervisor/admin only.
export const claimTask = (taskId: string): Promise<AnnotationTask> =>
  api.post(`/annotation/tasks/${taskId}/claim`).then((r) => r.data.data.task);

export const unclaimTask = (taskId: string): Promise<AnnotationTask> =>
  api.post(`/annotation/tasks/${taskId}/unclaim`).then((r) => r.data.data.task);

// ─── Utterances ────────────────────────────────────────────────────────────────

export const listUtterances = (taskId: string): Promise<Utterance[]> =>
  api.get(`/annotation/tasks/${taskId}/utterances`).then((r) => r.data.data);

export const createUtterance = (taskId: string, data: Partial<Utterance>): Promise<Utterance> =>
  api.post(`/annotation/tasks/${taskId}/utterances`, data).then((r) => r.data.data);

export const updateUtterance = (
  taskId: string,
  utteranceId: string,
  data: Partial<Utterance>,
): Promise<Utterance> =>
  api
    .put(`/annotation/tasks/${taskId}/utterances/${utteranceId}`, data)
    .then((r) => r.data.data);

export const deleteUtterance = (taskId: string, utteranceId: string): Promise<void> =>
  api.delete(`/annotation/tasks/${taskId}/utterances/${utteranceId}`).then(() => undefined);

export const reorderUtterances = (taskId: string, order: string[]): Promise<void> =>
  api
    .put(`/annotation/tasks/${taskId}/utterances/reorder`, { order })
    .then(() => undefined);

// ─── Entities ──────────────────────────────────────────────────────────────────

export const createEntity = (
  utteranceId: string,
  data: { entityType: string; entityName: string; sentiment?: string },
): Promise<Entity> =>
  api.post(`/annotation/utterances/${utteranceId}/entities`, data).then((r) => r.data.data);

export const updateEntity = (
  utteranceId: string,
  entityId: string,
  data: Partial<Entity>,
): Promise<Entity> =>
  api
    .put(`/annotation/utterances/${utteranceId}/entities/${entityId}`, data)
    .then((r) => r.data.data);

export const deleteEntity = (utteranceId: string, entityId: string): Promise<void> =>
  api
    .delete(`/annotation/utterances/${utteranceId}/entities/${entityId}`)
    .then(() => undefined);

// ─── Auto-save ─────────────────────────────────────────────────────────────────

export const autosave = (
  taskId: string,
  stateJson: object,
): Promise<{ saved: boolean; savedAt: string }> =>
  api.put(`/annotation/tasks/${taskId}/autosave`, { stateJson }).then((r) => r.data.data);

export const getAutosave = (
  taskId: string,
): Promise<{ stateJson: object; savedAt: string } | null> =>
  api.get(`/annotation/tasks/${taskId}/autosave`).then((r) => r.data.data ?? null);

// ─── Photos ────────────────────────────────────────────────────────────────────

export const listPhotos = (taskId: string): Promise<Photo[]> =>
  api.get(`/annotation/tasks/${taskId}/photos`).then((r) => r.data.data);

export const upsertPhotoLabels = (
  photoId: string,
  data: Partial<PhotoLabels>,
  photoType?: PhotoType | null,
): Promise<{ labels: PhotoLabels; photoType: PhotoType | null }> =>
  api
    .put(`/annotation/photos/${photoId}/labels`, {
      ...data,
      ...(photoType !== undefined ? { photoType } : {}),
    })
    .then((r) => r.data.data);

export const reviewPhoto = (
  photoId: string,
  data: { verified: boolean; notes?: string },
): Promise<PhotoLabels> =>
  api.post(`/annotation/photos/${photoId}/review`, data).then((r) => r.data.data);

// ─── Assignments ───────────────────────────────────────────────────────────────

export const listAssignments = (params?: {
  enumeratorId?: string;
  supervisorId?: string;
}): Promise<SupervisorAssignment[]> =>
  api.get('/annotation/assignments', { params }).then((r) => r.data.data.assignments ?? []);

export const createAssignment = (data: {
  enumeratorId: string;
  supervisorId: string;
}): Promise<SupervisorAssignment> =>
  api.post('/annotation/assignments', data).then((r) => r.data.data);

export const deleteAssignment = (assignmentId: string): Promise<void> =>
  api.delete(`/annotation/assignments/${assignmentId}`).then(() => undefined);

// ─── Transcriber Assignments (transcriber transcribes for which enumerator) ────

export interface TranscriberAssignment {
  assignmentId: string;
  enumeratorId: string;
  transcriberId: string;
  assignedBy: string;
  createdAt: string;
}

export const listTranscriberAssignments = (params?: {
  enumeratorId?: string;
  transcriberId?: string;
  // Pass supervisorId === my own userId to bulk-fetch every transcriber_assignments
  // row in the supervisor's subtree in one round-trip. Used by MyTeam and the
  // analytics page to avoid the per-transcriber N+1 loop.
  supervisorId?: string;
}): Promise<TranscriberAssignment[]> =>
  api.get('/annotation/transcriber-assignments', { params }).then((r) => r.data.data.assignments ?? []);

export const createTranscriberAssignment = (data: {
  enumeratorId: string;
  transcriberId: string;
}): Promise<TranscriberAssignment> =>
  api.post('/annotation/transcriber-assignments', data).then((r) => r.data.data);

export const deleteTranscriberAssignment = (assignmentId: string): Promise<void> =>
  api.delete(`/annotation/transcriber-assignments/${assignmentId}`).then(() => undefined);

// ─── Transcriber→Supervisor Assignments (which supervisor reviews a transcriber) ─

export interface TranscriberSupervisorAssignment {
  assignmentId: string;
  transcriberId: string;
  supervisorId: string;
  assignedBy: string;
  createdAt: string;
}

export const listTranscriberSupervisorAssignments = (params?: {
  transcriberId?: string;
  supervisorId?: string;
}): Promise<TranscriberSupervisorAssignment[]> =>
  api.get('/annotation/transcriber-supervisor-assignments', { params }).then((r) => r.data.data.assignments ?? []);

export const createTranscriberSupervisorAssignment = (data: {
  transcriberId: string;
  supervisorId: string;
}): Promise<TranscriberSupervisorAssignment> =>
  api.post('/annotation/transcriber-supervisor-assignments', data).then((r) => r.data.data);

export const deleteTranscriberSupervisorAssignment = (assignmentId: string): Promise<void> =>
  api.delete(`/annotation/transcriber-supervisor-assignments/${assignmentId}`).then(() => undefined);

// ─── Auto-Transcription ────────────────────────────────────────────────────────

export const triggerTranscription = (
  taskId: string,
  opts: { language?: string; maxSpeakers?: number } = {},
): Promise<{ jobId: string; transcriptionStatus: string }> =>
  api
    .post(`/annotation/tasks/${taskId}/transcribe`, {
      // Omit language → backend falls back to the session's language / auto.
      ...(opts.language ? { language: opts.language } : {}),
      ...(opts.maxSpeakers ? { maxSpeakers: opts.maxSpeakers } : {}),
    })
    .then((r) => r.data.data);

export const getTranscriptionJob = (taskId: string): Promise<TranscriptionJob | null> =>
  api
    .get(`/annotation/tasks/${taskId}/transcription-job`)
    .then((r) => r.data.data.job ?? null);

export const listAutoUtterances = (taskId: string): Promise<AutoUtterance[]> =>
  api
    .get(`/annotation/tasks/${taskId}/auto-utterances`)
    .then((r) => r.data.data.autoUtterances ?? []);

// ─── Sub-Admin scope assignments (admin-only) ──────────────────────────────────

export interface SubAdminAssignment {
  assignmentId: string;
  subadminId: string;
  supervisorId: string;
  assignedBy: string;
  createdAt: string;
}

export const listSubadminAssignments = (params?: {
  subadminId?: string;
  supervisorId?: string;
}): Promise<SubAdminAssignment[]> =>
  api.get('/admin/subadmin-assignments', { params }).then((r) => r.data.data.assignments ?? []);

export const createSubadminAssignment = (data: {
  subadminId: string;
  supervisorId: string;
}): Promise<SubAdminAssignment> =>
  api.post('/admin/subadmin-assignments', data).then((r) => r.data.data.assignment);

export const deleteSubadminAssignment = (assignmentId: string): Promise<void> =>
  api.delete(`/admin/subadmin-assignments/${assignmentId}`).then(() => undefined);

// ─── Supervisor-scoped transcriber assignments ─────────────────────────────────

export const supervisorCreateTranscriberAssignment = (data: {
  enumeratorId: string;
  transcriberId: string;
}): Promise<TranscriberAssignment> =>
  api.post('/supervisor/transcriber-assignments', data).then((r) => r.data.data.assignment);

export const supervisorDeleteTranscriberAssignment = (assignmentId: string): Promise<void> =>
  api.delete(`/supervisor/transcriber-assignments/${assignmentId}`).then(() => undefined);

// ─── Supervisor Analytics (admin + sub-admin) ──────────────────────────────────

export interface SupervisorAnalyticsRow {
  supervisorId: string;
  supervisorName: string;
  supervisorEmail: string;
  teamEnumerators: number;
  teamTranscribers: number;
  teamSize: number;
  // Recording volume in seconds, bucketed by pipeline stage (rendered as hours).
  recordingSeconds: number;
  submittedSeconds: number;
  supervisorApprovedSeconds: number;
  rejectedSeconds: number;
  sessionsUploaded: number;
  tasksSubmitted: number;
  tasksSupervisorApproved: number;
  tasksFinallyAccepted: number;
  avgHoursToSupervisorApprove: number | null;
  avgHoursToFinalAccept: number | null;
}

export const getSupervisorAnalytics = (params?: {
  startDate?: string;
  endDate?: string;
}): Promise<SupervisorAnalyticsRow[]> =>
  api.get('/admin/supervisor-analytics', { params }).then((r) => r.data.data.supervisors ?? []);

// A single supervisor's own recording-hour buckets — computed by the same
// backend function as the admin analytics table, so the values match. Used by
// the dashboard for supervisors (self-scope) with an optional date range.
export interface SupervisorHours {
  supervisorId: string;
  recordingSeconds: number;
  submittedSeconds: number;
  supervisorApprovedSeconds: number;
  rejectedSeconds: number;
  submittedSessions: number;
  rejectedSessions: number;
}

export const getMySupervisorHours = (params?: {
  startDate?: string;
  endDate?: string;
}): Promise<SupervisorHours> =>
  api.get('/supervisor/my-hours', { params }).then((r) => r.data.data);

// Unified recording-hour buckets for the CALLER'S scope (admin → platform,
// sub-admin → their supervisors, supervisor → own team). One endpoint, one
// backend function — the dashboard shows the same numbers for every role.
export interface RecordingHours {
  recordingSeconds: number;
  submittedSeconds: number;
  supervisorApprovedSeconds: number;
  rejectedSeconds: number;
  submittedSessions: number;
  rejectedSessions: number;
}

export const getRecordingHours = (params?: {
  startDate?: string;
  endDate?: string;
}): Promise<RecordingHours> =>
  api.get('/annotation/analytics/recording-hours', { params }).then((r) => r.data.data);

// Survey analytics — per-supervisor uploaded volume for each survey project in a
// window. Scoped by role on the backend (admin=all, sub-admin=their supervisors,
// supervisor=own survey projects/team).
export interface SurveyProjectSupervisorRow {
  supervisorId: string;
  supervisorName: string;
  supervisorEmail: string;
  sessions: number;
  seconds: number;
}
export interface SurveyProjectAnalytics {
  projectId: string;
  name: string;
  status: string;
  voiceOnly: boolean;
  totalSessions: number;
  totalSeconds: number;
  supervisors: SurveyProjectSupervisorRow[];
}

export const getSurveyAnalytics = (params?: {
  startDate?: string;
  endDate?: string;
}): Promise<SurveyProjectAnalytics[]> =>
  api.get('/admin/survey-analytics', { params }).then((r) => r.data.data.projects ?? []);
